<template>
    <div>
        <LabelField :label="$t('login_lock_status_label')">
            <div class="d-flex align-center flex-wrap" style="gap: 12px">
                <span>{{ statusText }}</span>
                <PlainBtn v-if="isLocked" :disabled="disabled" @click="openDialog">
                    {{ $t('login_lock_reset') }}
                </PlainBtn>
            </div>
        </LabelField>

        <v-dialog v-model="showDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>{{ $t('login_lock_reset_title') }}</v-card-title>
                <v-card-text>
                    <p>{{ $t('login_lock_reset_text') }}</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <PlainBtn :disabled="loading" @click="showDialog = false">{{ $t('cancel') }}</PlainBtn>
                    <ActionBtn :loading="loading" :disabled="loading" @click="submit">
                        {{ $t('login_lock_reset') }}
                    </ActionBtn>
                </v-card-actions>
                <v-fade-transition>
                    <v-card-text v-if="error">
                        <v-alert type="error">{{ error }}</v-alert>
                    </v-card-text>
                </v-fade-transition>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { instance as api } from '@/helpers/api';
import { errToStr } from '@/helpers/tools';
import dayjs from 'dayjs';

/**
 * Shows whether a user's login is temporarily locked (too many failed logins) and lets
 * an admin clear the lock. Use case: the support phone call - a user locked himself
 * out (or was locked out) and has to get in before the lock expires. Clearing the lock
 * also resets the failed login counter; the password stays untouched.
 *
 * Emits 'reset' after a successful reset, so the parent can refresh its record.
 */
export default {
    name: 'LoginLockReset',
    props: {
        /** the locked_until value of the user record (dayjs, invalid if not locked) */
        lockedUntil: {
            type: Object,
            default: null,
        },
        /** the user to unlock */
        userId: {
            type: Number,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showDialog: false,
            loading: false,
            error: null,
            /**
             * Set after a successful reset. The status is shown from here instead of writing
             * the record: the reset already happened on the server, and touching the record
             * would mark it as changed although nothing has to be saved.
             */
            wasReset: false,
        };
    },
    computed: {
        isLocked() {
            return (
                !this.wasReset &&
                !!this.lockedUntil &&
                this.lockedUntil.isValid() &&
                this.lockedUntil.isAfter(dayjs())
            );
        },
        statusText() {
            if (!this.isLocked) {
                return this.$t('login_lock_status_not_locked');
            }
            return this.$t('login_lock_status_locked', {
                date: this.lockedUntil.formatIf('DD.MM.YYYY HH:mm'),
            });
        },
    },
    methods: {
        openDialog() {
            this.error = null;
            this.showDialog = true;
        },
        async submit() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.error = null;
            try {
                await api().post('/User/resetLoginLock', { id: this.userId });
                this.showDialog = false;
                this.wasReset = true;
                this.$toast({ message: this.$t('login_lock_reset_success'), color: 'success' });
                this.$emit('reset');
            } catch (e) {
                this.error = errToStr(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
